import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { api, CATEGORIES } from "../../lib/api";
import { useSeo, pageSeo, breadcrumbLd } from "../../lib/seo";
import { track } from "../../lib/analytics";
import { ExperienceCard, PageHero, Empty, FaqList, Breadcrumbs, Spinner } from "../../components/site/Bits";
import { CorporateForm } from "../../components/site/Forms";
import { Testimonials } from "./Home";

const SUBS = { tours: [["weekend", "Weekend getaways"], ["multi-day", "Multi-day packages"]], "heritage-walks": [["fort", "Fort walks"], ["landmark", "Historical landmarks"], ["cultural", "Cultural & architectural trails"]],
  camping: [["lakeside", "Lakeside"], ["mountain", "Mountain"], ["forest", "Forest"]], "corporate-trek": [["team-building", "Team building"], ["leadership", "Leadership"]] };
const FILTERS = { trek: ["destination", "difficulty", "duration", "price", "date"], tours: ["sub", "destination", "duration", "price", "date"], "heritage-walks": ["sub", "destination", "duration", "language", "date"],
  camping: ["sub", "destination", "price", "date", "participants"], "corporate-trek": ["destination", "duration", "participants"], "": ["destination", "difficulty", "duration", "price", "date", "participants"] };

export default function CategoryPage({ category = "" }) {
  const { site } = useSite();
  const cat = CATEGORIES.find((c) => c.key === category);
  const svc = site?.services?.[category] || {};
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [dests, setDests] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [tms, setTms] = useState([]);
  const [showF, setShowF] = useState(false);
  const params = Object.fromEntries(sp.entries());
  const title = cat ? svc.title || cat.label : "All Experiences";
  useSeo({ ...pageSeo(site, category || "experiences", { title, description: svc.intro || "Search treks, tours, heritage walks, camping and corporate adventures.", ogImage: svc.hero_image }),
    robots: Object.keys(params).length ? "noindex,follow" : pageSeo(site, category).robots,
    jsonLd: breadcrumbLd([{ name: "Home", to: "/" }, { name: title, to: cat?.path || "/experiences" }]) }, [site, category, sp.toString()]);

  useEffect(() => {
    setData(null);
    api.get("/public/experiences", { params: { category, limit: 12, ...params } }).then((r) => setData(r.data));
  }, [category, sp.toString()]); // eslint-disable-line
  useEffect(() => {
    api.get("/public/destinations").then((r) => setDests(r.data));
    if (category) {
      api.get("/public/faqs", { params: { scope: category } }).then((r) => setFaqs(r.data));
      api.get("/public/testimonials", { params: { scope: category } }).then((r) => setTms(r.data));
    }
  }, [category]);

  const setP = (k, v) => {
    const n = new URLSearchParams(sp);
    v ? n.set(k, v) : n.delete(k);
    if (k !== "page") n.delete("page");
    setSp(n);
    if (k === "q" && v) track("search", { search_term: v, category });
  };
  const show = FILTERS[category] || FILTERS[""];
  const page = Number(params.page || 1);
  const relatedDests = dests.filter((d) => (data?.items || []).some((e) => e.destination?.id === d.id));

  return (
    <>
      <PageHero eyebrow={cat ? "Experiences" : "Search"} title={title} text={cat ? svc.intro : "Every published experience in one place."} image={svc.hero_image || "/images/hero.jpg"} alt={title}>
        {category === "corporate-trek" && <a href="#proposal" className="btn-light mt-10" data-testid="corporate-hero-cta">Request a Corporate Proposal</a>}
      </PageHero>
      <div className="container-x pt-6"><Breadcrumbs items={[{ name: "Home", to: "/" }, { name: title, to: "#" }]} /></div>
      <section className="py-12 lg:py-16 bg-white">
        <div className="container-x grid lg:grid-cols-12 gap-10">
          <aside className={`lg:col-span-3 ${showF ? "" : "hidden lg:block"}`} data-testid="filters-panel">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div><label className="field-label" htmlFor="f-q">Search</label><input id="f-q" className="field" placeholder="Name or place" defaultValue={params.q || ""} onKeyDown={(e) => e.key === "Enter" && setP("q", e.target.value)} onBlur={(e) => e.target.value !== (params.q || "") && setP("q", e.target.value)} data-testid="filter-search" /></div>
              {show.includes("sub") && SUBS[category] && <Sel id="sub" label="Type" v={params.subcategory} on={(v) => setP("subcategory", v)} opts={SUBS[category]} />}
              {show.includes("destination") && <Sel id="destination" label="Destination" v={params.destination} on={(v) => setP("destination", v)} opts={dests.map((d) => [d.slug, d.name])} />}
              {show.includes("difficulty") && <Sel id="difficulty" label="Difficulty" v={params.difficulty} on={(v) => setP("difficulty", v)} opts={[["easy", "Easy"], ["moderate", "Moderate"], ["challenging", "Challenging"], ["difficult", "Difficult"]]} />}
              {show.includes("duration") && <Sel id="duration" label="Duration" v={params.duration} on={(v) => setP("duration", v)} opts={[["1", "1 day or less"], ["2-3", "2–3 days"], ["4+", "4+ days"]]} />}
              {show.includes("language") && <Sel id="language" label="Language" v={params.language} on={(v) => setP("language", v)} opts={[["English", "English"], ["Marathi", "Marathi"], ["Hindi", "Hindi"]]} />}
              {show.includes("price") && (
                <div><span className="field-label">Price per person (₹)</span><div className="grid grid-cols-2 gap-2">
                  <input className="field" type="number" min="0" placeholder="Min" defaultValue={params.min_price || ""} onBlur={(e) => setP("min_price", e.target.value)} data-testid="filter-min-price" />
                  <input className="field" type="number" min="0" placeholder="Max" defaultValue={params.max_price || ""} onBlur={(e) => setP("max_price", e.target.value)} data-testid="filter-max-price" />
                </div></div>
              )}
              {show.includes("date") && <div><label className="field-label" htmlFor="f-date">Available from</label><input id="f-date" className="field" type="date" min={new Date().toISOString().slice(0, 10)} value={params.date || ""} onChange={(e) => setP("date", e.target.value)} data-testid="filter-date" /></div>}
              {show.includes("participants") && <div><label className="field-label" htmlFor="f-p">Group size</label><input id="f-p" className="field" type="number" min="1" defaultValue={params.participants || ""} onBlur={(e) => setP("participants", e.target.value)} data-testid="filter-participants" /></div>}
              <button className="btn-secondary w-full" onClick={() => setSp(new URLSearchParams())} data-testid="filter-clear">Clear filters</button>
            </div>
          </aside>
          <div className="lg:col-span-9">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <p className="text-sm text-[#666]" data-testid="results-count">{data ? `${data.total} experience${data.total === 1 ? "" : "s"}` : "Loading…"}</p>
              <div className="flex gap-3">
                <button className="btn-secondary btn-sm lg:hidden" onClick={() => setShowF(!showF)} data-testid="toggle-filters"><SlidersHorizontal className="h-4 w-4" />Filters</button>
                <select className="field !w-auto !py-1.5" value={params.sort || "featured"} onChange={(e) => setP("sort", e.target.value)} aria-label="Sort" data-testid="sort-select">
                  <option value="featured">Featured</option><option value="price_asc">Price: low to high</option><option value="price_desc">Price: high to low</option><option value="duration">Shortest first</option><option value="newest">Newest</option>
                </select>
              </div>
            </div>
            {!data ? <Spinner /> : !data.items.length ? (
              <Empty title="No matching experiences" text="Try removing a filter, or tell us what you're looking for."><Link to="/contact" className="btn-primary">Send an enquiry</Link></Empty>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="results-grid">{data.items.map((e, i) => <ExperienceCard key={e.id} e={e} i={i} />)}</div>
            )}
            {data?.pages > 1 && (
              <nav className="mt-12 flex gap-2" aria-label="Pagination" data-testid="pagination">
                {Array.from({ length: data.pages }, (_, i) => i + 1).map((n) => (
                  <button key={n} onClick={() => setP("page", n === 1 ? "" : String(n))} aria-current={n === page ? "page" : undefined} className={`h-10 w-10 border border-black text-sm ${n === page ? "bg-black text-white" : "bg-white hover:bg-black hover:text-white"}`}>{n}</button>
                ))}
              </nav>
            )}
          </div>
        </div>
      </section>
      {relatedDests.length > 0 && (
        <section className="py-16 bg-[#F5F5F5]"><div className="container-x">
          <p className="eyebrow mb-4">Related destinations</p>
          <div className="flex flex-wrap gap-3">{relatedDests.map((d) => <Link key={d.id} to={`/destinations/${d.slug}`} className="btn-secondary btn-sm">{d.name}</Link>)}</div>
        </div></section>
      )}
      {category === "corporate-trek" && (
        <section id="proposal" className="py-24 bg-black text-white on-dark scroll-mt-20"><div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4"><p className="eyebrow !text-white/60 mb-3">Company event enquiry</p><h2 className="section-title">Request a Corporate Proposal</h2><p className="mt-6 text-white/75">Share your team size, dates and goals. We'll respond with a customised plan and quotation.</p></div>
          <div className="lg:col-span-8 bg-white text-black p-6 sm:p-10"><CorporateForm /></div>
        </div></section>
      )}
      {tms.length > 0 && <Testimonials items={tms} />}
      {faqs.length > 0 && (
        <section className="py-24 bg-white"><div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4"><p className="eyebrow mb-3">FAQs</p><h2 className="section-title">{title} questions</h2></div>
          <div className="lg:col-span-8"><FaqList faqs={faqs} /></div>
        </div></section>
      )}
    </>
  );
}

const Sel = ({ id, label, v, on, opts }) => (
  <div>
    <label className="field-label" htmlFor={`f-${id}`}>{label}</label>
    <select id={`f-${id}`} className="field" value={v || ""} onChange={(e) => on(e.target.value)} data-testid={`filter-${id}`}>
      <option value="">Any</option>
      {opts.map(([val, l]) => <option key={val} value={val}>{l}</option>)}
    </select>
  </div>
);
